/**
 * Dropdown lists drawn by the page instead of the operating system.
 *
 * Native <select> menus on macOS/Windows (Safari, Firefox, Chrome < 135) are
 * rendered with OS fonts and ignore web fonts, so Lao and Thai options do not
 * show in Noto Sans. This replaces the open menu of every single-choice
 * <select> with an HTML list that uses the app font and theme. The <select>
 * itself stays the source of truth: choosing an option sets its value and
 * fires `input`/`change`, so React forms work unchanged.
 *
 * Skipped when the browser can style the menu itself (appearance: base-select,
 * see global.css), on touch screens (their native pickers are easier to use),
 * and for <select multiple>, <select size>, disabled or `data-native` selects.
 */

const MENU_CLASS = 'select-menu';

let menu: HTMLDivElement | null = null;
let owner: HTMLSelectElement | null = null;
let active = -1;
let typed = '';
let typedAt = 0;
let cleanup: (() => void) | null = null;

function enabledFor(sel: HTMLSelectElement): boolean {
  if (sel.multiple || sel.size > 1 || sel.disabled || sel.dataset.native !== undefined) return false;
  if (CSS.supports?.('appearance', 'base-select')) return false;
  return window.matchMedia('(pointer: fine)').matches;
}

function items(): HTMLElement[] {
  return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="option"]')) : [];
}

function setActive(index: number, scroll = true) {
  const list = items();
  if (!list.length) return;
  const i = Math.max(0, Math.min(list.length - 1, index));
  list[active]?.classList.remove('active');
  active = i;
  list[i].classList.add('active');
  menu?.setAttribute('aria-activedescendant', list[i].id);
  if (scroll) list[i].scrollIntoView({ block: 'nearest' });
}

function nextEnabled(from: number, step: number): number {
  const list = items();
  for (let i = from + step; i >= 0 && i < list.length; i += step) {
    if (list[i].getAttribute('aria-disabled') !== 'true') return i;
  }
  return from;
}

function choose(el: HTMLElement) {
  const sel = owner;
  if (!sel || el.getAttribute('aria-disabled') === 'true') return;
  const index = Number(el.dataset.index);
  close();
  if (sel.selectedIndex !== index) {
    // The native setter keeps React's value tracker in sync.
    const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'selectedIndex')!.set!;
    setter.call(sel, index);
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function position(sel: HTMLSelectElement) {
  if (!menu) return;
  const r = sel.getBoundingClientRect();
  const gap = 4;
  const margin = 8;
  menu.style.minWidth = `${r.width}px`;
  menu.style.maxWidth = `${Math.max(r.width, window.innerWidth - 2 * margin)}px`;
  const below = window.innerHeight - r.bottom - gap - margin;
  const above = r.top - gap - margin;
  const natural = Math.min(menu.scrollHeight, 360);
  const placeBelow = below >= Math.min(natural, 200) || below >= above;
  const max = Math.max(120, Math.min(360, placeBelow ? below : above));
  menu.style.maxHeight = `${max}px`;
  const height = Math.min(natural, max);
  menu.style.top = `${placeBelow ? r.bottom + gap : r.top - gap - height}px`;
  const width = menu.offsetWidth;
  menu.style.left = `${Math.max(margin, Math.min(r.left, window.innerWidth - width - margin))}px`;
}

export function close(refocus = true) {
  if (!menu) return;
  const sel = owner;
  cleanup?.();
  cleanup = null;
  menu.remove();
  menu = null;
  owner = null;
  active = -1;
  sel?.setAttribute('aria-expanded', 'false');
  if (refocus) sel?.focus({ preventScroll: true });
}

function open(sel: HTMLSelectElement) {
  close(false);
  owner = sel;
  const id = `select-menu-${Math.random().toString(36).slice(2, 8)}`;
  menu = document.createElement('div');
  menu.className = MENU_CLASS;
  menu.id = id;
  menu.tabIndex = -1;
  menu.setAttribute('role', 'listbox');
  const label = sel.getAttribute('aria-label') ?? (sel.labels?.[0]?.textContent || '');
  if (label) menu.setAttribute('aria-label', label);
  const lang = sel.closest('[lang]')?.getAttribute('lang');
  if (lang) menu.lang = lang;

  let groupEl: HTMLOptGroupElement | null = null;
  Array.from(sel.options).forEach((opt, index) => {
    const group = opt.parentElement instanceof HTMLOptGroupElement ? opt.parentElement : null;
    if (group && group !== groupEl) {
      const head = document.createElement('div');
      head.className = `${MENU_CLASS}-group`;
      head.textContent = group.label;
      menu!.appendChild(head);
    }
    groupEl = group;
    if (opt.hidden) return;
    const el = document.createElement('div');
    el.id = `${id}-${index}`;
    el.className = `${MENU_CLASS}-option`;
    el.setAttribute('role', 'option');
    el.dataset.index = String(index);
    el.textContent = opt.label || opt.text;
    const disabled = opt.disabled || !!group?.disabled;
    if (disabled) el.setAttribute('aria-disabled', 'true');
    if (index === sel.selectedIndex) {
      el.setAttribute('aria-selected', 'true');
      el.classList.add('selected');
    }
    if (group) el.classList.add('in-group');
    menu!.appendChild(el);
  });

  // Inside an open <dialog> the menu must join its top layer to be visible.
  const host = sel.closest('dialog[open]') ?? document.body;
  host.appendChild(menu);
  sel.setAttribute('aria-expanded', 'true');
  sel.setAttribute('aria-controls', id);
  position(sel);
  const list = items();
  const current = list.findIndex((el) => el.classList.contains('selected'));
  setActive(current >= 0 ? current : nextEnabled(-1, 1));
  menu.focus({ preventScroll: true });

  const onMenuMouseDown = (e: MouseEvent) => e.preventDefault(); // keep focus, no text selection
  const onMenuClick = (e: MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[role="option"]');
    if (el) choose(el);
  };
  const onMenuMove = (e: MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[role="option"]');
    if (el && el.getAttribute('aria-disabled') !== 'true') setActive(items().indexOf(el), false);
  };
  const onKey = (e: KeyboardEvent) => menuKey(e);
  const onOutside = (e: Event) => {
    if (menu && !menu.contains(e.target as Node) && e.target !== owner) close(false);
  };
  const onScroll = (e: Event) => {
    if (menu && !menu.contains(e.target as Node)) close(false);
  };
  const onResize = () => close(false);
  menu.addEventListener('mousedown', onMenuMouseDown);
  menu.addEventListener('click', onMenuClick);
  menu.addEventListener('mousemove', onMenuMove);
  menu.addEventListener('keydown', onKey);
  document.addEventListener('mousedown', onOutside, true);
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);
  window.addEventListener('blur', onResize);
  cleanup = () => {
    document.removeEventListener('mousedown', onOutside, true);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('blur', onResize);
  };
}

function menuKey(e: KeyboardEvent) {
  const list = items();
  switch (e.key) {
    case 'ArrowDown':
      setActive(nextEnabled(active, 1));
      break;
    case 'ArrowUp':
      setActive(nextEnabled(active, -1));
      break;
    case 'Home':
      setActive(nextEnabled(-1, 1));
      break;
    case 'End':
      setActive(nextEnabled(list.length, -1));
      break;
    case 'PageDown':
      setActive(Math.min(list.length - 1, active + 10));
      break;
    case 'PageUp':
      setActive(Math.max(0, active - 10));
      break;
    case 'Enter':
    case ' ':
      if (list[active]) choose(list[active]);
      break;
    case 'Escape':
      close();
      break;
    case 'Tab':
      close();
      return; // let focus move on
    default:
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Type-ahead: jump to the next option starting with the typed text.
        const now = Date.now();
        typed = now - typedAt > 700 ? e.key : typed + e.key;
        typedAt = now;
        const q = typed.toLocaleLowerCase();
        const n = list.length;
        // A single key cycles through matches; a longer prefix refines from the current one.
        const start = typed.length > 1 ? Math.max(0, active) : active + 1;
        for (let k = 0; k < n; k++) {
          const el = list[(start + k) % n];
          if (el.getAttribute('aria-disabled') !== 'true' && (el.textContent ?? '').replace(/[\u200b-\u200d\ufeff]/g, '').toLocaleLowerCase().startsWith(q)) {
            setActive((start + k) % n);
            break;
          }
        }
        break;
      }
      return;
  }
  e.preventDefault();
  e.stopPropagation();
}

function targetSelect(e: Event): HTMLSelectElement | null {
  const t = e.target;
  return t instanceof HTMLSelectElement && enabledFor(t) ? t : null;
}

let installed = false;

export function installSelectMenu() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  document.addEventListener(
    'mousedown',
    (e) => {
      const sel = targetSelect(e);
      if (!sel || e.button !== 0) return;
      e.preventDefault(); // suppress the OS menu
      if (owner === sel) {
        close();
        return;
      }
      sel.focus({ preventScroll: true });
      open(sel);
    },
    true,
  );
  document.addEventListener(
    'keydown',
    (e) => {
      const sel = targetSelect(e);
      if (!sel || menu) return;
      const opens = e.key === ' ' || e.key === 'Enter' || e.key === 'F4' || e.key === 'ArrowDown' || e.key === 'ArrowUp';
      if (!opens || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      open(sel);
    },
    true,
  );
}
